import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/16/solid'
import { Button, ButtonText } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { Skeleton } from '@/components/ui/skeleton'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import { cn } from '@/lib/utils'
import useMobileTable, { type MobileTable } from './useMobileTable'
import { TabletPeriodControls } from './PeriodControls'
import VaultScopeList from './VaultScopeList'
import {
  amountClass,
  discrepancyIsGood,
  isActiveTarget,
  rowBackground,
  targetPath,
} from './rowStyle'
import type { TableRowModel } from './tableRows'

const WHOLE = { minimumFractionDigits: 0, maximumFractionDigits: 0 }
const DISCREPANCY = { ...WHOLE, addParanthesesForNegative: false }
const VISIBLE = 4

export default function TabletTable() {
  const table = useMobileTable({ visible: VISIBLE })
  const navigate = useNavigate()
  const location = useLocation()
  const money = useNumberFormatter(WHOLE)
  const money2 = useNumberFormatter({})
  const diffFormat = useNumberFormatter(DISCREPANCY)

  const first = table.offsets[0]
  const closing = table.rows.find((r) => r.key === 'closing')?.cells[0]
  const net = table.rows.find((r) => r.key === 'net')?.cells[0]

  const open = (row: TableRowModel, offset: number) => {
    if (row.expanded !== undefined) table.toggleRow(row.key)
    else if (row.target) navigate(targetPath(row.target, offset))
  }

  return (
    <>
      <TabletPeriodControls table={table}>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" dropdown className="rounded-lg">
              <ButtonText>{table.scope.label}</ButtonText>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[280px] px-3 py-1">
            <VaultScopeList table={table} compact />
          </PopoverContent>
        </Popover>
      </TabletPeriodControls>

      <div className="flex min-h-0 flex-1 bg-layout-secondary">
        <div className="pf-mobile-scroll min-w-0 flex-1 overflow-auto border-r border-primary bg-white pb-6 pl-4 pt-4">
          <div className="flex items-end border-b border-primary pb-2.5 pl-3 pr-4">
            <div className="flex-1 text-[10px] font-semibold tracking-[.09em] text-txt-placeholder">
              VAULTS &amp; CATEGORIES
            </div>
            {table.heads.map((h) => (
              <div
                key={h.offset}
                className="flex w-[92px] flex-col gap-0.5 text-right"
              >
                <span
                  className={cn(
                    'text-[12.5px] font-bold',
                    h.isNow ? 'text-txt-accent' : 'text-txt-primary',
                  )}
                >
                  {h.label}
                </span>
                <span className="truncate text-[9.5px] font-semibold tracking-[.05em] text-txt-placeholder">
                  {h.short}
                </span>
              </div>
            ))}
          </div>

          {table.isLoading ? (
            <div className="flex flex-col gap-2 pr-4 pt-3">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            table.rows.map((row, i) => (
              <TabletRow
                key={row.key}
                row={row}
                first={i === 0}
                firstOffset={first}
                money={money}
                diffFormat={diffFormat}
                isActive={(offset) =>
                  isActiveTarget(
                    row.target,
                    offset,
                    location.pathname,
                    location.search,
                  )
                }
                onOpen={(offset) => open(row, offset)}
              />
            ))
          )}
        </div>

        <SidePanel
          table={table}
          closing={money2(closing?.amount ?? 0)}
          net={net?.amount ?? 0}
          hasActuals={first !== undefined && first <= 0}
          netFormat={diffFormat}
        />
      </div>
    </>
  )
}

function TabletRow({
  row,
  first,
  firstOffset,
  money,
  diffFormat,
  isActive,
  onOpen,
}: {
  row: TableRowModel
  first: boolean
  firstOffset: number
  money: (n: number) => string
  diffFormat: (n: number) => string
  isActive: (offset: number) => boolean
  onOpen: (offset: number) => void
}) {
  const isCap = row.kind === 'cap' || row.kind === 'net' || row.kind === 'closing'
  const collapsible = row.expanded !== undefined
  const cellTargets = row.kind === 'leaf' && !!row.target

  return (
    <div
      className={cn(
        'flex items-center pr-4',
        rowBackground(row),
        isCap
          ? cn(
              'rounded-[10px] pl-3',
              row.kind === 'closing' ? 'h-11' : row.kind === 'net' ? 'mt-4 h-[42px]' : 'h-9',
              row.kind === 'cap' && !first && 'mt-4',
            )
          : cn(
              'border-b border-light',
              row.kind === 'group' && 'h-[42px] pl-3.5',
              row.kind === 'leaf' && 'h-[46px]',
              row.kind === 'leaf' && (row.depth > 0 ? 'pl-[34px]' : 'pl-3.5'),
              row.kind === 'add' && 'h-10 pl-3.5',
            ),
      )}
    >
      <button
        type="button"
        disabled={!collapsible && !row.target}
        onClick={() => onOpen(firstOffset)}
        aria-expanded={row.expanded}
        className="flex min-w-0 flex-1 items-center gap-2 self-stretch text-left disabled:cursor-default"
      >
        {collapsible &&
          (row.expanded ? (
            <ChevronDownIcon className="size-3 shrink-0 text-txt-placeholder" />
          ) : (
            <ChevronRightIcon className="size-3 shrink-0 text-txt-placeholder" />
          ))}
        <span
          className={cn(
            'line-clamp-2 break-words leading-tight',
            isCap &&
              'text-[11px] font-medium uppercase tracking-[.09em] text-txt-secondary',
            row.kind === 'closing' && 'text-txt-primary',
            row.kind === 'group' && 'text-[13px] font-medium text-txt-primary',
            row.kind === 'leaf' && 'text-[13.5px] font-semibold text-txt-primary',
            row.kind === 'add' && 'text-[13.5px] font-semibold text-txt-accent',
          )}
        >
          {row.kind === 'add' ? `＋ ${row.name}` : row.name}
        </span>
      </button>

      {row.cells.map((cell) => {
        const content = (
          <>
            <span
              className={cn(
                'font-semibold tabular-nums',
                isCap ? 'text-[13px]' : 'text-[14px]',
                amountClass(row, cell),
              )}
            >
              {cell.amount === null || cell.amount === 0
                ? '—'
                : money(cell.amount)}
            </span>
            {cell.discrepancy !== null && (
              <span
                className={cn(
                  'text-[10.5px] font-semibold tabular-nums',
                  discrepancyIsGood(row, cell.discrepancy)
                    ? 'text-txt-success'
                    : 'text-txt-error',
                )}
              >
                {cell.discrepancy > 0 ? '+' : '−'}
                {diffFormat(Math.abs(cell.discrepancy))}
              </span>
            )}
          </>
        )
        const cellClass = cn(
          'flex w-[92px] flex-col items-end gap-px rounded-lg px-2 py-[3px]',
          cell.negative && 'bg-yellow-lowest',
        )
        return cellTargets ? (
          <button
            type="button"
            key={cell.offset}
            onClick={() => onOpen(cell.offset)}
            className={cn(
              cellClass,
              'hover:bg-gray-tr-08',
              isActive(cell.offset) &&
                'bg-accent-tr-08 ring-1 ring-inset ring-[hsl(var(--border-accent))]',
            )}
          >
            {content}
          </button>
        ) : (
          <div key={cell.offset} className={cellClass}>
            {content}
          </div>
        )
      })}
    </div>
  )
}

function SidePanel({
  table,
  closing,
  net,
  hasActuals,
  netFormat,
}: {
  table: MobileTable
  closing: string
  net: number
  hasActuals: boolean
  netFormat: (n: number) => string
}) {
  return (
    <div className="pf-mobile-scroll flex w-[280px] shrink-0 flex-col gap-3.5 overflow-y-auto p-4">
      <Card className="w-full rounded-2xl">
        <div className="flex flex-col gap-1.5 px-4 pb-4 pt-3.5">
          <span className="text-[10px] font-semibold tracking-[.1em] text-txt-placeholder">
            CLOSING BALANCE
          </span>
          <div className="flex items-baseline justify-between gap-2.5">
            <span className="text-[22px] font-bold tabular-nums tracking-[-.02em] text-txt-primary">
              {closing}
            </span>
            {hasActuals ? (
              <span
                className={cn(
                  'text-sm font-bold tabular-nums',
                  net < 0 ? 'text-txt-error' : 'text-txt-success',
                )}
              >
                {net < 0 ? '−' : '+'}
                {netFormat(Math.abs(net))}
              </span>
            ) : (
              <span className="text-[12.5px] font-semibold text-txt-placeholder">
                Estimate
              </span>
            )}
          </div>
        </div>
      </Card>

      <div className="flex flex-col">
        <div className="flex items-center justify-between pb-1.5">
          <span className="text-[10px] font-semibold tracking-[.1em] text-txt-placeholder">
            VAULTS
          </span>
          <button
            type="button"
            className="text-xs font-semibold text-txt-accent"
            onClick={table.scope.toggleAll}
          >
            {table.scope.allSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>
        <VaultScopeList table={table} compact />
      </div>

      <p className="text-xs leading-normal text-txt-placeholder">
        Tap a figure to add or review transactions for that period.
      </p>
    </div>
  )
}
